import comm from "csi/comm";
import Spec from "csi/core/Spec";
import csi from "csi/main";
import { TYPE_TASKS } from "csi/reflection/reflectionConstants";
import reflectionHints from "csi/reflectionHints";
import { featureTogglesStore } from "csi/state/rootStore";
import { ValuesResult } from "csi/store/Values";
import { C8Node } from "csi/typings";
import xml from "csi/xml";
import { ILocaleProvider } from "designSystem/locale";
import { get, set } from "idb-keyval";
import _ from "lodash";
import { cnl, VALUE_TYPE } from "src/c8-platform/type/value";

/** the limit of recent values */
const RECENT_VALUES_LIMIT = 10;

/** map to hold the breadcrumb nodes for the current task node  */
let CACHED_TASK_BREADCRUMB_MAP: Record<cnl, C8Node[]> = {};

/** blank related values */
const BLANK_RELATED_VALUES = new Set([xml.VALUE_BLANK, xml.VALUE_NONBLANK]);

type GroupItemsFunc = (options: object, items: ValuesResult[]) => ValuesResult[];
export type SelectedFilterItems = ValuesResult[];
type RecentFilterItems = ValuesResult[];
type ActualFilterItems = ValuesResult[];
type AddSelectedValueFunc = (value: string, text: string, bNew: boolean) => void;

/** create ValueResult item */
export function createValueResultItem(
	index: number,
	value: string,
	text: string,
	newProperty: string,
	cssProperty: string,
	newCss: string,
	exclusive: boolean = false,
	addSelectedValueFunc?: AddSelectedValueFunc,
): ValuesResult {
	const isMatchValue = xml.queryIsMatchValue(value);

	if (isMatchValue) {
		text = xml.queryFromMatchValue(value);
	}

	const valueResultItem: ValuesResult = {
		_count: 1,
		_exclusive: exclusive,
		_index: index,
		_refs: undefined,
		_text: text,
		_textCount: text,
		_value: value,
	};

	if (isMatchValue) {
		valueResultItem[cssProperty] = newCss;
		valueResultItem[newProperty] = true;

		// add it to the store, we need to ensure the custom value is in the store, whether it is queried
		// or not so that setCheckedValue would work as expected in _ComboBoxCheckedMenu.js
		addSelectedValueFunc?.(value, text, true);
	}

	return valueResultItem;
}

/** determine if the spec is part of the applicable type list to enable recents and delayed filtering  */
function isApplicableTypes(spec: Spec): boolean {
	const {
		STRING, STRING_LIST, STRING_MAP, STRING_SET, STRING_VECT,
		REF, REF_LIST, REF_MAP, REF_SET, REF_VECT,
	} = VALUE_TYPE;
	const { type } = spec;

	const typeList: Set<string> = new Set([
		STRING, STRING_LIST, STRING_MAP, STRING_SET, STRING_VECT,
		REF, REF_LIST, REF_MAP, REF_SET, REF_VECT,
	]);

	return typeList.has(type);
}

/** determine whether or not enable the delay filtering functionality, if false, it behaves the same as before */
export function enableDelayFiltering(spec: Spec): boolean {
	if (featureTogglesStore.DISABLE_DELAYED_FILTERING) {
		return false;
	}

	return isApplicableTypes(spec);
}

/** determine whether or not to show the recent values */
export function enableRecents(spec: Spec): boolean {
	if (featureTogglesStore.DISABLE_RECENTS) {
		return false;
	}

	return isApplicableTypes(spec);
}

/** generate recent values and actual values to show in ad-hoc filter */
export function generateHybridItems(
	allItems: ValuesResult[],
	items: ValuesResult[],
	selectedItems: ValuesResult[],
	recentValues: Set<string>,
	checkedValues: string[],
	storeOptions: object,
	groupFunc: GroupItemsFunc,
	locale: ILocaleProvider,
	newProperty: string,
	cssProperty: string,
	labelProperty: string,
	newCss: string,
	addSelectedItemFunc: AddSelectedValueFunc,
	putItemCallback: (item: ValuesResult) => void,
): [SelectedFilterItems, RecentFilterItems, ActualFilterItems] {
	let index = items.length;
	const recentResults: ValuesResult[] = [];
	let itemsExcludeRecentValues: ValuesResult[] = [];

	// make the most recent selection shows up first
	const finalSet = new Set([...recentValues].reverse());
	const itemsMap: _.Dictionary<ValuesResult> = _.keyBy(items, (x) => x._value);
	const allItemsMap: _.Dictionary<ValuesResult> = _.keyBy(allItems, (x) => x._value);
	const indexSet = new Set(Object.keys(itemsMap));
	const checkedValuesSet = new Set(checkedValues);

	for (const item of finalSet) {
		// skip the selected ones
		if (checkedValuesSet.has(item)) {
			continue;
		}

		// the recent values may not exist in the store, skip it, it'll be evicted down the road anyway
		if (!allItemsMap[item]) {
			continue;
		}

		const text = allItemsMap[item][labelProperty];
		const singleEntry = createValueResultItem(index, item, text, newProperty, cssProperty, newCss, false, addSelectedItemFunc);

		delete itemsMap[item];
		recentResults.push(singleEntry);
		index++;
	}

	// remove the selected ones as well
	checkedValues.forEach((checkedValue) => {
		delete itemsMap[checkedValue];
	});

	itemsExcludeRecentValues = Object.values(itemsMap);

	// group with recent values 
	const storeOptionsForRecentValues = {
		...storeOptions,
		group: {
			id: (item) => 1,
			nameFunc: (id) => locale.resource("Recents"),
		},
	};

	const groupedRecentItems = groupFunc(storeOptionsForRecentValues, recentResults);

	// group with the rest values
	const storeOptionsForRest: Record<string, any> = {
		...storeOptions,
		group: {
			id: (item) => csi.MENU_ITEM_SEPARATOR,
		},
	};

	// set nextResponseOptions to null so that the items can be grouped properly
	storeOptionsForRest.nextResponseOptions = null;
	const groupedRestItems = groupFunc(storeOptionsForRest, itemsExcludeRecentValues);

	// selected items
	const finalSelectedItems = generateSelectedValues(selectedItems);

	// lastly, add the items in recent to the store, because those might not exist anymore in the latest store, however, we need to make sure these are added to the store, so that the CMS would work
	for (const entry of recentResults) {
		const { _value } = entry;
		const isMatchValue = xml.queryIsMatchValue(_value);

		if (isMatchValue) {
			// the custom ones would be taken care of during adding them as custom filters
			continue;
		}

		if (indexSet.has(_value)) {
			// if still exists in the store, skip it, otherwise, the order would mess up
			continue;
		}

		putItemCallback(entry);
	}

	return [finalSelectedItems, groupedRecentItems, groupedRestItems];
}

/** generate recent values in the delayed filtering case */
export function generateRecentValues(
	originalRecentItems: ValuesResult[],
	selectedItems: ValuesResult[],
	checkedValues: string[],
	storeOptions: object,
	groupFunc: GroupItemsFunc,
): [SelectedFilterItems, RecentFilterItems] {
	const copiedItems = [...originalRecentItems];
	const itemsMap: _.Dictionary<ValuesResult> = _.keyBy(copiedItems, (x) => x._value);

	const blank = itemsMap[xml.VALUE_BLANK];
	const nonBlank = itemsMap[xml.VALUE_NONBLANK];

	checkedValues.forEach((checkedValue) => {
		delete itemsMap[checkedValue];
	});

	// blank/non blank would be in a separate group, exclude them first
	delete itemsMap[xml.VALUE_BLANK];
	delete itemsMap[xml.VALUE_NONBLANK];

	const newRecentItems = Object.values(itemsMap);
	let groupedNewRecentItems = [];

	if (newRecentItems.length > 0) {
		// group the rest other than blank/non blank
		groupedNewRecentItems = groupFunc(storeOptions, newRecentItems);
	}

	const selectItemMap = _.keyBy(selectedItems, (x) => x._value);

	// we would handle the blank/non blank in the selected items too, if they are not part of that list, handle them here
	if (!selectItemMap[xml.VALUE_BLANK]) {
		groupedNewRecentItems.push(blank);
	}

	if (!selectItemMap[xml.VALUE_NONBLANK]) {
		groupedNewRecentItems.push(nonBlank);
	}

	// selected items
	const finalSelectedItems = generateSelectedValues(selectedItems);

	return [finalSelectedItems, groupedNewRecentItems];
}

/** generate the selected values, normally need to filter out the special items, for example, the blank/non blank item */
function generateSelectedValues(originalSelectedItems: ValuesResult[]): SelectedFilterItems {
	const copiedSelectedItems = _.keyBy([...originalSelectedItems], (x) => x._value);

	const valueBlank = copiedSelectedItems[xml.VALUE_BLANK];
	const valueNonBlank = copiedSelectedItems[xml.VALUE_NONBLANK];
	delete copiedSelectedItems[xml.VALUE_BLANK];
	delete copiedSelectedItems[xml.VALUE_NONBLANK];

	const finalSelectedItems = Object.values(copiedSelectedItems);

	// add the blank/non blank values as first
	if (valueBlank) {
		finalSelectedItems.unshift(valueBlank);
	}

	if (valueNonBlank) {
		finalSelectedItems.unshift(valueNonBlank);
	}

	return finalSelectedItems;
}

/**
 * get the keys to the recent histories 
 * @param rootNode  - the root node of the view which we're currently on
 * @param userCnl - the cnl of the current user
 * @param spec - the spec of the attribute we're currently filtering by
 * @param locationNodes - the location nodes
 * @param subtypeName - subtype name
 * @param read  - true by default, get the keys to read the recent values, otherwise, set it to false to get the keys to update the recent values
 */
export async function getDelayFilteringKeysForAttribute(rootNode: C8Node, userCnl: cnl, spec: Spec, locationNodes: C8Node[], subtypeName: string, read: boolean = true): Promise<Set<string>> {
	const copiedNode = [...locationNodes];
	let hierarchyNodes = copiedNode.reverse();
	let currentNode = hierarchyNodes[0];

	if (currentNode.$Type === TYPE_TASKS) {
		// the task view is different from others, need to do something special
		// request server to guarantee the breadcrumb nodes are correct and to be in cache
		// for example, the new hierarchy for Food (ProductHierarchy), they actually don’t own the Food. Instead, Food references it. 
		// Also, for Materials they no longer are owned by a Material Security Group - instead they reference it.
		currentNode = rootNode;
		const currentNodeURL = currentNode.$URL;
		const cachedHierarchyNodes = CACHED_TASK_BREADCRUMB_MAP[currentNodeURL];

		if (!!cachedHierarchyNodes) {
			hierarchyNodes = cachedHierarchyNodes;
		} else {
			const request = comm.getBreadcrumbRequest(currentNode.$URL, null, null);
			const response = await request.go();
			hierarchyNodes = response.getResultNodes();

			// clear the map first to avoid growing too big 
			CACHED_TASK_BREADCRUMB_MAP = {};
			CACHED_TASK_BREADCRUMB_MAP[currentNodeURL] = hierarchyNodes;
		}
	}

	const nodeTypeOfFilteringAttribute = spec.getQuerySpecClass()
		.getType();

	const attributeId = spec.getKey();
	const keys: Set<string> = new Set();
	const baseKey = `${userCnl}-${nodeTypeOfFilteringAttribute}-${attributeId}`;

	const buildKeyForNode = (node: C8Node): string => {
		//  global list
		let key = `${baseKey}`;

		if (!reflectionHints.isTypeOfLandingPage(node.$Type)) {
			// instance level and other normal global level
			key = `${key}-${node.$URL}`;
		}

		if (!!subtypeName) {
			// append more to be able to identify subtype
			key = `${key}-${subtypeName}`;
		}

		return key;
	};

	if (read) {
		// get the key for reading
		const instanceKey = buildKeyForNode(currentNode);
		keys.add(instanceKey);
	} else {
		// get the key for writing - calculate the keys to which the value would be applied
		hierarchyNodes.forEach(((currentHierarchy) => {
			const instanceKey = buildKeyForNode(currentHierarchy);
			keys.add(instanceKey);
		}));

		// alway update the global level when writing
		keys.add(baseKey);
	}

	return keys;
}

/** get the recent values based on the keys */
export async function getRecentValues(keysToRecentValues: Set<string>, flatResult: boolean = true): Promise<Set<string>[] | Set<string>> {
	const finalValues: Set<string>[] = [];
	const finalFlatValues: Set<string> = new Set();

	for (const keyToRecentValues of keysToRecentValues) {
		const currentValues = await get<Set<string>>(keyToRecentValues) || new Set<string>();
		finalValues.push(currentValues);

		currentValues.forEach((element) => {
			finalFlatValues.add(element);
		});
	}

	return flatResult ? finalFlatValues : finalValues;
}

/**
 * process the selections for a particular attribute
 * @param values - the selections of user
 * @param keysToRecentValues - keys to recent values
 */
export async function processRecentSelections(values: string[], keysToRecentValues: Set<string>): Promise<Set<string>> {
	const sets = await getRecentValues(keysToRecentValues, false);

	if (!Array.isArray(values)) {
		values = [values];
	}

	// filter out the blank/non blank items
	values = values.filter((value) => !BLANK_RELATED_VALUES.has(value));

	const keyEntries = keysToRecentValues.entries();

	sets.forEach((currentSet) => {
		// iterate the set, add it to the bottom of list if it's already part of the set so that it'll be evict at last
		values.forEach((item) => {
			currentSet.delete(item);
			const reachLimit = currentSet.size == RECENT_VALUES_LIMIT;

			if (reachLimit) {
				// if react the limit, remove the oldest one
				for (const entry of currentSet.entries()) {
					currentSet.delete(entry[0]);

					// only remove the first one
					break;
				}
			}

			currentSet.add(item);
		});

		const keyToRecentValues = (keyEntries.next().value)[0];

		// update the entry
		set(keyToRecentValues, currentSet);
	});

	return sets[0];
}

/** create the value of custom filter */
export function createCustomFilterValue(value: string): string {
	return `*${value}*`;
}

/** check if this item is a custom filter in recent values */
export function isCustomFilterInRecentValues(item: string, recentValues: Set<string>): boolean {
	const text = createCustomFilterValue(item);

	return !!recentValues.has(text);
}

/** exclude the custom filters */
export function excludeCustomFilters(items: ValuesResult[]): ValuesResult[] {
	return items.filter((x) => !x._new);
}